// 📁 src/pages/MarketResearch/MarketResearch.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FrameworkSelector from "./FrameworkSelector";
import ModuleHeader from "../../components/ModuleHeader";
import { buildMDIHPdfName } from "../../utils/pdfNaming";
import { useAuth } from "../../context/AuthContext";
import { useIdea } from "../../context/IdeaContext";
import axios from "../../utils/axiosInstance";
import { PlayCircle,CheckCircle,Download,ArrowRight,Sparkles } from "lucide-react";

const MarketResearch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedIdea, marketResearch, markModuleComplete } = useIdea();
  const passedIdeaText = "";

  const [ideaText, setIdeaText] = useState(""); // description for UI/PDF
  const [ideaContext,setIdeaContext] = useState(""); // full AI context
  const [fiveP, setFiveP] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExport,setLoadingExport]=useState(false);
  const [loadedFromDB,setLoadedFromDB] = useState(false);

  const { user } = useAuth();
  const isAuthenticated = !!user?._id;

  /* ------------------------------------------------------------- */
  useEffect(() => {

      if(!selectedIdea || !selectedIdea.ideaId){

      console.warn("⚠️ No selected idea. Redirecting.");

      navigate("/idea-generation");

      return;

      }

      const title =
      selectedIdea.ideaTitle || "";

      const description =
      selectedIdea.solution || "";

      const context = `

      ${selectedIdea.ideaTitle}

      Problem: ${selectedIdea.problem}

      Target User: ${selectedIdea.targetUser}

      Industry: ${selectedIdea.industry}

      Solution: ${selectedIdea.solution}

      `;

      setIdeaText(description);

      setIdeaContext(context);

      if(selectedIdea?.ideaTitle){

        localStorage.setItem(
        "activeIdeaTitle",
        selectedIdea.ideaTitle
        );

        }
      loadExistingResearch(selectedIdea.ideaId);

      suggestFrameworks(context);

      },[selectedIdea,navigate]);

  /* ------------------------------------------------------------- */
  const loadExistingResearch = async (ideaId) => {
    try {
      const res = await axios.get(
        `/api/market-research/my-research?ideaId=${ideaId}`
      );

      if (res.data?.success && res.data.research) {
        setFiveP(res.data.research);
        setRecordId(res.data.recordId);
        setLoadedFromDB(true);
        markModuleComplete("marketResearch", res.data.research);
      }
    } catch (err) {
      console.log("ℹ️ No saved research for this idea yet.");
    }
  };

  /* ------------------------------------------------------------- */
  const handleAnalyze = async () => {
    if (!ideaText.trim()) return;

    if (!isAuthenticated) {
      alert("⚠️ Please log in to run Market Research.");
      return navigate("/login");
    }

    setLoading(true);
    try {
      const res = await axios.post("/api/market-research", {
        ideaId: selectedIdea.ideaId,
        ideaText: ideaContext.trim(),
      });

      setFiveP(res.data.research);
      setRecordId(res.data.id);

      markModuleComplete("marketResearch", res.data.research);

      localStorage.setItem(
        "latestMarketResearch",
        JSON.stringify({
          ideaId: selectedIdea.ideaId,
          ideaText: ideaText,
          recordId: res.data.id,
        })
      );

      suggestFrameworks(ideaText);
    } catch (err) {
      console.error("❌ 5P Analysis failed:", err);
      alert("Failed to run analysis. Please try again.");
    } finally {
      setLoading(false);
    }
  };
     /* ----------------------------------------------------------
DOWNLOAD PDF ⭐ UNIVERSAL STANDARD (BUSINESS MODEL PATTERN)
---------------------------------------------------------- */

        const handleDownloadPDF = async () => {

        if(!selectedIdea?.ideaId){

        alert("Idea missing");

        return;

        }

        try{

        setLoadingExport(true);

        const token = localStorage.getItem("token");

        const response = await axios.post(

        "/api/pdf/market-research",

        {

        ideaId:selectedIdea.ideaId,

        recordId,

        framework:"5P"

        },

        {

        headers:{
        Authorization:`Bearer ${token}`
        },

        responseType:"blob"

        }

        );

        const blob = new Blob(
        [response.data],
        {type:"application/pdf"}
        );

        const url =
        window.URL.createObjectURL(blob);

        const link =
        document.createElement("a");

        link.href = url;

        link.download =
        buildMDIHPdfName(

        selectedIdea?.ideaTitle ||
        selectedIdea?.title ||
        "Startup",

        "Market_Research_5P"

        );

        document.body.appendChild(link);

        link.click();

        link.remove();

        }
        catch(err){

        console.error(
        "PDF download failed:",
        err
        );

        alert("Failed to download PDF");

        }
        finally{

        setLoadingExport(false);

        }

        };
  /* ------------------------------------------------------------- */
  const suggestFrameworks = (text) => {
    const frameworks = [
      { name: "SWOT", keywords: ["strength", "weakness", "competition"] },
      { name: "TAM-SAM-SOM", keywords: ["market", "size", "growth"] },
      { name: "Lean Canvas", keywords: ["startup", "validation", "structure"] },
      { name: "Porter", keywords: ["industry", "rivalry", "supplier"] },
      { name: "JTBD", keywords: ["needs", "experience", "motivation"] },
      { name: "PESTEL", keywords: ["macro", "regulation", "trends"] },
    ];

    const lower = (text || "").toLowerCase();

    const matched = frameworks
      .filter((fw) => fw.keywords.some((kw) => lower.includes(kw)))
      .map((fw) => fw.name);

    setRecommended(matched.length ? matched : ["Lean Canvas", "TAM-SAM-SOM"]);
  };

  /* ------------------------------------------------------------- */
  useEffect(() => {
    const saved = JSON.parse(
      localStorage.getItem("latestMarketResearch") || "{}"
    );

    if (saved.ideaId && saved.ideaId === selectedIdea?.ideaId) {
      setIdeaText(saved.ideaText || ideaText);
      setRecordId(saved.recordId);
      loadExistingResearch(saved.ideaId);
    }
  }, [selectedIdea]);

  /* ------------------------------------------------------------- */
  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">

        {/* ===== HERO ===== */}
        <ModuleHeader

          title="Market Research"

          subtitle="Validate your startup idea using structured market research frameworks."

          currentModule="marketResearch"

          isLoadedFromDB={loadedFromDB}

          />

          <div className="text-sm text-gray-500">

          <b>Startup:</b>

          {
          selectedIdea?.ideaTitle ||
          selectedIdea?.title ||
          location?.state?.ideaTitle ||
          localStorage.getItem("activeIdeaTitle") ||
          "Startup Idea"
          }

          </div>

        {/* ===== INPUT SECTION ===== */}
        <div className="space-y-4 border-b border-slate-200 pb-6">
          <h2 className="text-2xl font-semibold text-blue-900">
            Refine Your Idea
          </h2>

          <input
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg 
                       focus:ring-2 focus:ring-blue-500 focus:outline-none 
                       text-sm text-slate-700 bg-white"
            type="text"
            value={ideaText}
            onChange={(e) => setIdeaText(e.target.value)}
            placeholder="Enter or refine your startup idea..."
          />

          <button
            onClick={handleAnalyze}
            disabled={loading || !ideaText}
            className="bg-blue-600 text-white px-7 py-2.5 rounded-lg 
                       text-sm font-semibold hover:bg-blue-700 
                       transition disabled:opacity-50"
          >
            {loading ? "Analyzing..." : "Run 5P Analysis"}
          </button>
        </div>

        {/* ===== 5P INSIGHTS (Polished Cards) ===== */}
        {fiveP && (
          <div className="space-y-6 border-b border-slate-200 pb-6">

            <h2 className="text-3xl font-semibold text-blue-900">
              5P Insights
            </h2>

            <div className="space-y-5 text-sm leading-relaxed">

              {/* Pain */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h4 className="text-blue-900 font-semibold mb-1">Pain</h4>
                <p className="text-slate-600">{fiveP.painArea}</p>
              </div>

              {/* Purpose */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h4 className="text-blue-900 font-semibold mb-1">Purpose</h4>
                <p className="text-slate-600">{fiveP.purpose}</p>
              </div>

              {/* Potential */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h4 className="text-blue-900 font-semibold mb-1">Potential</h4>
                <p className="text-slate-600">{fiveP.potential}</p>
              </div>

              {/* People */}
              <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                <h4 className="text-blue-900 font-semibold">People</h4>

                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                    Target Audience
                  </p>
                  <p className="text-slate-600">
                    {fiveP.people?.targetAudience}
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                    Competitors
                  </p>
                  <ul className="list-disc ml-5 text-slate-600">
                    {fiveP.people?.competitors?.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Pay */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h4 className="text-blue-900 font-semibold mb-1">Pay</h4>
                <p className="text-slate-600">{fiveP.pay}</p>
              </div>

            </div>
          </div>
        )}

        {/* ===== FRAMEWORKS ===== */}
        {fiveP && (
          <div className="space-y-4">

            <h3 className="text-xl font-semibold text-blue-900">
              Framework Suggestions
            </h3>

            {recommended.length > 0 && (
              <p className="text-sm text-slate-600">
                Recommended: {recommended.join(", ")}
              </p>
            )}

            <FrameworkSelector
              ideaId={selectedIdea.ideaId}
              idea={ideaText}
              recordId={recordId}
              recommended={recommended}
            />
            <button

            onClick={handleDownloadPDF}

            disabled={loadingExport}

            className="mt-4 bg-green-600 hover:bg-blue-700
            text-white px-6 py-3 rounded-lg font-semibold
            inline-flex items-center gap-2 transition
            disabled:opacity-50"

            >

            <Download size={18}/>

            {loadingExport

            ? "Preparing PDF..."

            : "Download Market Research (5P) Report"}

            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default MarketResearch;
